import type {SessionState} from '@github-ui/agent-sessions/types/session'

import type {AgentTask} from '../../api/memex-items/contracts'
import type {MemexItemModel} from '../../models/memex-item-model'

/**
 * Returns whether a task should be shown based on its state.
 * Currently shows all tasks that have a valid state.
 */
export function shouldShowAgentTaskPill(state: SessionState | undefined): state is SessionState {
  if (!state) return false
  return true
}

/** Maximum number of agent task sub-rows to display per issue. */
export const MAX_AGENT_TASK_SUB_ROWS = 5

/**
 * Returns true when a task is "unhydrated": the backend sent a task id but no
 * state yet, so the client still needs to lazy-load its state. Only meaningful
 * when `memex_lazy_hydrate_agent_tasks` is enabled.
 *
 * A task whose hydration terminally failed (`hydrationFailed`) is not considered
 * unhydrated — it should be hidden rather than re-rendered as loading.
 */
export function isUnhydratedAgentTask(task: AgentTask): boolean {
  return !!task.id && !task.state && !task.hydrationFailed
}

/**
 * Returns whether a given agent task entry should be visible.
 *
 * A task is visible when it has a real state, or — when lazy hydration is
 * enabled — when it is still unhydrated (so it can render a loading affordance
 * instead of disappearing during the hydration window). A task whose hydration
 * terminally failed is hidden (it is no longer "unhydrated").
 */
export function isVisibleAgentTask(task: AgentTask, lazyHydrationEnabled = false): boolean {
  if (shouldShowAgentTaskPill(task.state)) return true
  return lazyHydrationEnabled && isUnhydratedAgentTask(task)
}

/**
 * Returns true if the task is "in progress" (not yet completed/failed/cancelled/timed_out).
 * These tasks have priority when capping the visible list.
 */
export function isProgressTask(task: AgentTask): boolean {
  return (
    task.state === 'in_progress' ||
    task.state === 'queued' ||
    task.state === 'waiting_for_user' ||
    task.state === 'idle'
  )
}

/**
 * Returns up to MAX_AGENT_TASK_SUB_ROWS visible agent tasks for an item, prioritizing
 * in-progress tasks over completed/failed/cancelled ones when more than the max exist.
 */
export function getVisibleAgentTasks(model: MemexItemModel, lazyHydrationEnabled = false): Array<AgentTask> {
  const tasks = model.agentTasks ?? []

  const visible = tasks.filter(task => isVisibleAgentTask(task, lazyHydrationEnabled))

  if (visible.length <= MAX_AGENT_TASK_SUB_ROWS) {
    return visible
  }

  // More than max: prioritize in-progress tasks over completed ones
  const inProgress = visible.filter(isProgressTask)
  const completed = visible.filter(t => !isProgressTask(t))

  return [...inProgress, ...completed].slice(0, MAX_AGENT_TASK_SUB_ROWS)
}

/**
 * Returns the state of the most recently created agent task
 */
export function getLatestAgentTaskState(tasks: Array<AgentTask> | undefined): SessionState | null {
  if (!tasks || tasks.length === 0) return null

  const latest = tasks.reduce((a, b) => (new Date(b.createdAt ?? 0) > new Date(a.createdAt ?? 0) ? b : a))
  return latest.state ?? null
}
